import { Box, Card, Heading, Paragraph, Text, Tooltip } from '@zillow/constellation-10';
import type { BackgroundReport } from '@zg-rentals/ts-capps-client';
import { getReportIdsText, getReportStatusDescription } from '@zg-rentals/applications-utils';
import { UserType } from '../../models/report-models';

const conditionalApprovalTooltipText =
  'This information is unavailable because your area requires you to conditionally approve this application before you can see the Criminal Report. You can take action below in the criminal records section.';

export function BackgroundReportSummary({
  userType,
  backgroundReport,
  zipCode,
  requestedDateTime,
  expirationDateTime,
  requiresConditionalApproval,
}: {
  userType: UserType;
  backgroundReport: BackgroundReport;
  zipCode: string;
  requestedDateTime: string;
  expirationDateTime: string;
  requiresConditionalApproval: boolean;
}) {
  const evictionRecordsCount = backgroundReport?.evictionReport?.records?.length || 0;
  const criminalRecordsCount = backgroundReport?.criminalReport?.records?.length || 0;
  const criminalRecordUnavailableDescription =
    requiresConditionalApproval && userType === UserType.Landlord
      ? 'Unavailable'
      : getReportStatusDescription(backgroundReport?.criminalReport?.reportStatus);
  const housingCourtRecordUnavailableDescription = getReportStatusDescription(
    backgroundReport?.evictionReport?.reportStatus,
  );
  const reportIds = getReportIdsText([
    backgroundReport?.criminalReport?.responseId,
    backgroundReport?.evictionReport?.responseId,
  ]);

  return (
    <>
      <Heading level={2} textStyle="heading-xs" css={{ marginTop: 'loose', marginBottom: 'loose' }}>
        Background check summary
      </Heading>
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'default', marginBottom: 'default' }}>
        <Box css={{ gridColumnEnd: { base: 'span 2', md: 'span 1' } }}>
          <Card tone="soft" elevated={false}>
            <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <Paragraph>Housing court records</Paragraph>
              <Text textStyle="heading-md" css={{ marginTop: 'default', marginBottom: 'default' }}>
                {housingCourtRecordUnavailableDescription ?? evictionRecordsCount}
              </Text>
            </Box>
          </Card>
        </Box>
        <Box css={{ gridColumnEnd: { base: 'span 2', md: 'span 1' } }}>
          <Card tone="soft" elevated={false}>
            <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Paragraph css={{ marginRight: 'tighter' }}>Criminal records</Paragraph>
                {requiresConditionalApproval && userType === UserType.Landlord && (
                  <Tooltip content={conditionalApprovalTooltipText} />
                )}
              </Box>
              <Text textStyle="heading-md" css={{ marginTop: 'default', marginBottom: 'default' }}>
                {criminalRecordUnavailableDescription ?? criminalRecordsCount}
              </Text>
            </Box>
          </Card>
        </Box>
      </Box>
      <Heading level={2} textStyle="heading-xs" css={{ marginTop: 'loose', marginBottom: 'loose' }}>
        Report detail
      </Heading>
      <Paragraph>Report ID: {reportIds}</Paragraph>
      <Paragraph>Rental property ZIP Code: {zipCode}</Paragraph>
      <Paragraph>Created: {requestedDateTime}</Paragraph>
      <Paragraph>Expiry: {expirationDateTime}</Paragraph>
    </>
  );
}
